import {
    AfterViewInit,
    Component,
    DestroyRef,
    ElementRef,
    InputSignal,
    OutputEmitterRef,
    Signal,
    computed,
    contentChildren,
    effect,
    inject,
    input,
    output,
    viewChild, ChangeDetectionStrategy
} from "@angular/core";
import {MessageDetails, MessageLayout, MessageLevel} from "./message";
import {NotificationService} from "../notification.service";
import {IconButtonType} from "../../buttons/icon-button/icon-button";
import {LinkComponent} from "../../buttons/link/link.component";
import {onEach} from '../../utils/observable-utils';
import {ActivatableDirective} from '../../ui-elements/activatable.directive';

@Component({
    template: "<div #messageBody></div>",
    host: {
        "[class.closable]": "closable()",
        "[class.actionable]": "actionable()",
        "[class.errorLevel]": "isErrorLevel()",
        "[class.warningLevel]": "isWarningLevel()",
        "[class.successLevel]": "isSuccessLevel()",
        "[class.infoLevel]": "isInfoLevel()",
        "[class.horizontal]": "isHorizontalLayout()",
        "[class.vertical]": "isVerticalLayout()",
        "[class.link-horizontal]": "isHorizontalLinkLayout()",
        "[class.link-vertical]": "isVerticalLinkLayout()"
    },
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AbstractMessage extends ActivatableDirective implements AfterViewInit {
    private destroyRef: DestroyRef = inject(DestroyRef);
    private readonly element: ElementRef = inject(ElementRef);
    public notificationService: NotificationService = inject(NotificationService);

    IconButtonType = IconButtonType;

    public readonly message: InputSignal<MessageDetails | undefined> = input<MessageDetails | undefined>();
    public readonly level: InputSignal<MessageLevel> = input<MessageLevel>(MessageLevel.INFO);
    public readonly icon: InputSignal<string | undefined> = input<string | undefined>();
    public readonly text: InputSignal<string | undefined> = input<string | undefined>();
    public readonly translationParams: InputSignal<Record<string, string | number>> = input<Record<string, string | number>>({});
    public readonly showIcon: InputSignal<boolean> = input<boolean>(true);
    public readonly link: InputSignal<string | undefined> = input<string | undefined>();
    public readonly layout: InputSignal<MessageLayout> = input<MessageLayout>(MessageLayout.VERTICAL);
    public readonly linkLayout: InputSignal<MessageLayout | undefined> = input<MessageLayout | undefined>();
    public readonly closable: InputSignal<boolean> = input<boolean>(false);
    public readonly actionable: InputSignal<boolean> = input<boolean>(false);

    public onClose: OutputEmitterRef<void> = output<void>();
    public onLinkActivate: OutputEmitterRef<void> = output<void>();

    private messageBody: Signal<ElementRef> = viewChild.required<ElementRef>("messageBody");
    private links: Signal<ReadonlyArray<LinkComponent>> = contentChildren(LinkComponent);

    public displayLevel: Signal<MessageLevel> = computed(() => this.message()?.level ?? this.level());
    public displayIcon: Signal<string | undefined> = computed(() => this.message()?.icon ?? this.icon());
    public displayText: Signal<string | undefined> = computed(() => this.message()?.text ?? this.text());
    public displayTranslationParams: Signal<Record<string, string | number>> = computed(() => this.message()?.translationParams ?? this.translationParams());
    public displayShowIcon: Signal<boolean> = computed(() => this.message()?.showIcon ?? this.showIcon());
    public displayLink: Signal<string | undefined> = computed(() => this.message()?.link ?? this.link());
    public isErrorLevel: Signal<boolean> = computed(() => this.displayLevel() === MessageLevel.ERROR);
    public isWarningLevel: Signal<boolean> = computed(() => this.displayLevel() === MessageLevel.WARNING);
    public isSuccessLevel: Signal<boolean> = computed(() => this.displayLevel() === MessageLevel.SUCCESS);
    public isInfoLevel: Signal<boolean> = computed(() => this.displayLevel() === MessageLevel.INFO);
    public isHorizontalLayout: Signal<boolean> = computed(() => this.layout() === MessageLayout.HORIZONTAL);
    public isVerticalLayout: Signal<boolean> = computed(() => this.layout() === MessageLayout.VERTICAL);
    public isHorizontalLinkLayout: Signal<boolean> = computed(() => this.linkLayout() == undefined ? this.isHorizontalLayout() : (this.linkLayout() === MessageLayout.HORIZONTAL));
    public isVerticalLinkLayout: Signal<boolean> = computed(() => this.linkLayout() == undefined ? this.isVerticalLayout() : (this.linkLayout() === MessageLayout.VERTICAL));

    public iconName: Signal<string> = computed(() => {
        if (this.displayIcon()) {
            return this.displayIcon()!;
        }
        switch (this.displayLevel()) {
            case MessageLevel.ERROR:
                return "exclamation-circle";
            case MessageLevel.WARNING:
                return "warning-triangle";
            case MessageLevel.SUCCESS:
                return "check-circle";
            case MessageLevel.INFO:
                return "information-circle";
        }
    });

    constructor() {
        super();
        effect(() => this.setMaxHeight());
    }

    ngAfterViewInit(): void {
        onEach(this.notificationService.footerMessagesChanged$, () => this.setMaxHeight(), this.destroyRef);
        setTimeout(() => this.setMaxHeight());
    }

    private setMaxHeight(): void {
        this.links();
        if (this.message() && this.message()!.closed) {
            this.element.nativeElement.style.removeProperty("max-height");
            this.element.nativeElement.style.border = "none";
        } else if (this.messageBody().nativeElement.scrollHeight < 35) {
            setTimeout(() => this.setMaxHeight(), 500);
        } else {
            this.element.nativeElement.style.maxHeight = this.messageBody().nativeElement.scrollHeight + "px";
            this.element.nativeElement.style.removeProperty("border");
            setTimeout(() => this.setMaxHeight(), 500);
        }
    }

    public override activate(event: MouseEvent): boolean {
        if (!super.activate(event)) {
            return false;
        }
        if (this.closable()) {
            this.onClose.emit();
        } else if (this.message()?.onActivate && !this.message()!.closed) {
            this.notificationService.closeFooterMessage(this.message()!);
            this.message()!.onActivate();
        }
        return true;
    }

    public linkActivated(): void {
        if (this.message() && this.message()!.onActivate && !this.message()!.closed) {
            this.notificationService.closeFooterMessage(this.message()!);
            this.message()!.onActivate();
        } else {
            this.onLinkActivate.emit();
        }
    }
}

